import { useContext } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { GetProps } from '@tamagui/core';

import { Box } from '../../Box';
import { Button, ChevronBackButton } from '../../Button';
import type { ButtonProps } from '../../Button';
import { Form } from '../../Form';
import { useIsKeyboardVisible } from '../../shared/useKeyboardIsVisible';
import { LayoutContext } from './LayoutContext';
import { LayoutGroup } from './LayoutGroup';
import { LinearGradient } from './LinearGradient';
import { ProgressBar } from './ProgressBar';
import type { ProgressBarProps } from './ProgressBar';

const { formLayout: formLayoutTokens } = tokens;

export type BottomNavProps = (
  | {
      /**
       * Props for the submit button (required if not providing `children`).
       * Instead of passing `onPress` here, use the Form Provider's `onSubmit`
       */
      submitButtonProps: Omit<ButtonProps, 'size' | 'variant' | 'onPress'> &
        Partial<Pick<ButtonProps, 'size' | 'variant'>>;
      /**
       * Props for the secondary action button (only rendered if provided)
       */
      secondaryButtonProps?: Omit<ButtonProps, 'size' | 'variant'> &
        Partial<Pick<ButtonProps, 'size' | 'variant'>>;
      /**
       * Optional props for the chevron icon button (only rendered if provided)
       */
      backButtonProps?: Omit<GetProps<typeof ChevronBackButton>, 'size'> &
        Partial<Pick<GetProps<typeof ChevronBackButton>, 'size'>>;
    }
  | {
      /**
       * Children to render in the bottom nav. Use this if you need to render
       * something other than the default back and submit buttons
       */
      children: React.ReactNode;
    }
) & {
  /**
   * Optional props for the progress bar (only rendered if provided)
   */
  progressBarProps?: ProgressBarProps;
};

/**
 * Bottom nav for form pages. This component is positioned absolutely at the
 * bottom of its container. It accepts `backButtonProps` (optional) and
 * `submitButtonProps` (required) or `children` to render something custom.
 */
export const BottomNav = ({ progressBarProps, ...props }: BottomNavProps) => {
  const backButtonProps = 'backButtonProps' in props ? props.backButtonProps : undefined;
  const secondaryButtonProps =
    'secondaryButtonProps' in props ? props.secondaryButtonProps : undefined;
  const submitButtonProps = 'submitButtonProps' in props ? props.submitButtonProps : undefined;
  const children = 'children' in props ? props.children : undefined;

  const { topBottom, rightLeft } = formLayoutTokens.margin.nav.bottom;

  const { setBottomNavHeight } = useContext(LayoutContext);

  const isKeyboardVisible = useIsKeyboardVisible();

  return (
    <LayoutGroup
      position="absolute"
      pointerEvents="none"
      bottom={0}
      left={0}
      right={0}
      // Hide the bottom nav when the keyboard is visible
      height={isKeyboardVisible ? 0 : undefined}
      opacity={isKeyboardVisible ? 0 : 1}
      animation="accelerateDecelerateFastest"
      animateOnly={['opacity']}
      // Update layout context so that the background can adjust its padding
      onLayout={({
        nativeEvent: {
          layout: { height },
        },
      }) => {
        setBottomNavHeight?.(height);
      }}
    >
      <LinearGradient
        height={formLayoutTokens.nav.bottom.scrim.height}
        gradient={formLayoutTokens.color.scrim}
      />

      {progressBarProps && <ProgressBar {...progressBarProps} />}

      {children ? (
        children
      ) : (
        <Box
          // Ensures default nav buttons are still clickable
          pointerEvents="auto"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          backgroundColor={formLayoutTokens.color.background.nav.default}
          paddingVertical={topBottom.default}
          paddingHorizontal={rightLeft.xsmallviewport}
          $group-formLayout-largerThanXS={{
            paddingHorizontal: rightLeft.smallviewport,
          }}
          $group-formLayout-largerThanSM={{
            paddingHorizontal: rightLeft.mediumviewport,
          }}
          $group-formLayout-largerThanMD={{
            paddingHorizontal: rightLeft.largeviewport,
          }}
          $group-formLayout-largerThanLG={{
            paddingHorizontal: rightLeft.xlargeviewport,
          }}
        >
          <Box>
            {backButtonProps && (
              <ChevronBackButton
                size="medium"
                $group-formLayout-largerThanSM={{ frameSize: 'large' }}
                {...backButtonProps}
              />
            )}
          </Box>
          <Box flexDirection="row" gap={formLayoutTokens.padding.nav.bottom.buttons}>
            {secondaryButtonProps && (
              <Button
                data-testid="formLayout-secondaryButton"
                variant="secondary"
                size="small"
                $group-formLayout-largerThanSM={{ size: 'large' }}
                {...secondaryButtonProps}
              />
            )}
            {submitButtonProps && (
              <Form.Button
                variant="primary"
                size="small"
                $group-formLayout-largerThanSM={{ size: 'large' }}
                {...submitButtonProps}
              />
            )}
          </Box>
        </Box>
      )}
    </LayoutGroup>
  );
};
